import React, { useState, useCallback } from 'react';
import {
  getNotificationPrefs,
  setNotificationPrefs,
  requestNotificationPermission,
} from '../../services/notifications';
import { startPopularityChecker, stopPopularityChecker } from '../../services/popularityChecker';

const INTERVAL_OPTIONS = [
  { value: 5, label: '5 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
];

const NotificationSettings = () => {
  const [prefs, setPrefs] = useState(getNotificationPrefs);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const updatePrefs = useCallback((updates) => {
    setPrefs((prev) => {
      const next = { ...prev, ...updates };
      setNotificationPrefs(next);
      if (next.enabled) {
        startPopularityChecker();
      } else {
        stopPopularityChecker();
      }
      return next;
    });
  }, []);

  const handleToggle = async () => {
    if (!prefs.enabled) {
      const permission = await requestNotificationPermission();
      if (permission !== 'granted') {
        setPermissionDenied(true);
        return;
      }
      setPermissionDenied(false);
    }
    updatePrefs({ enabled: !prefs.enabled });
  };

  return (
    <div className="glass-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Quiet Place Alerts
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Get notified when favorites are below threshold
          </p>
        </div>
        <button
          onClick={handleToggle}
          className={`relative w-11 h-6 rounded-full transition-colors duration-300 min-h-[44px] min-w-[44px] flex items-center ${
            prefs.enabled ? 'bg-honey-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          role="switch"
          aria-checked={prefs.enabled}
          aria-label="Enable quiet place alerts"
        >
          <span
            className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${
              prefs.enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      {permissionDenied && (
        <p className="text-xs text-red-500">
          Notification permission denied. Please enable in browser settings.
        </p>
      )}

      {prefs.enabled && (
        <>
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>Threshold</span>
              <span>{prefs.threshold}%</span>
            </div>
            <input
              type="range"
              min="10"
              max="90"
              step="5"
              value={prefs.threshold}
              onChange={(e) => updatePrefs({ threshold: Number(e.target.value) })}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-honey-500"
              aria-label="Occupancy threshold percentage"
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>10%</span>
              <span>90%</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Check every</span>
            <select
              value={prefs.intervalMinutes}
              onChange={(e) => updatePrefs({ intervalMinutes: Number(e.target.value) })}
              className="p-2 min-h-[44px] glass rounded-lg text-xs text-gray-700 dark:text-gray-200
                focus:outline-none focus:ring-2 focus:ring-honey-400/50"
              aria-label="Check interval"
            >
              {INTERVAL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationSettings;
